import React from "react";
import { Button, Col, Form, Radio, Row, Space } from "antd";

import CustomSearchBox from "./components/CustomSearchBox";
import DiseaseGrid from "./components/DiseaseGrid/DiseaseGrid";
import LocationAutocomplete from "./components/LocationAutocomplete";
import StudyTypes from "./components/StudyTypes";
import { AgeOptions, DistanceOptions, GenderOptions } from "./constants";
import { filterUpdateEvent } from "@/utils/analytics/googleAnalytics";

import type { SearchFormStepsProps } from "./types";

function SearchFormSteps(props: SearchFormStepsProps) {
    const {
        form,
        initialState,
        labels,
        placeholders,
        hide,
        stepRefs,
        showSticky,
        conditions,
        scrollToTop,
        scrollToStep,
        onOpenDemo,
    } = props;

    const { searchLabel, locationLabel, distanceLabel, ageLabel, sexLabel, studyTypeLabel } = labels;
    const { conditionPlaceholder } = placeholders;
    const { hideSearchBar, hideDiseaseGrid, hideLocation, hideAge, hideSex, hideStudyType } = hide;

    return (
        <>
            <div ref={stepRefs.topOfMenu} />
            <Row justify="center">
                <Col>
                    <div className="text-center">
                        <h2 style={{ fontSize: "125%" }} className="mb-2 mt-2">
                            {searchLabel || "Type Any Condition, or Choose One of the Icons Below"}
                        </h2>
                        <div style={{ fontSize: "1.2rem" }} className="text-gray font-weight-medium">
                            Don&apos;t know where to start?
                            <Button
                                type="text"
                                style={{
                                    paddingInlineStart: ".25rem",
                                    paddingInlineEnd: ".25rem",
                                }}
                                onClick={onOpenDemo}
                            >
                                Watch our video guide
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row justify="center">
                <Col xs={24} sm={24} md={18} lg={16} xl={14} xxl={14}>
                    {!hideSearchBar && (
                        <Form.Item name="condition" valuePropName="value" trigger="onValueChange">
                            <CustomSearchBox
                                // Analytics are in component
                                handleSubmit={() => form.submit()}
                                placeholder={conditionPlaceholder}
                            />
                        </Form.Item>
                    )}
                </Col>
            </Row>
            {!hideDiseaseGrid && (
                <Row className="mt-4">
                    <Col span={24}>
                        <Form.Item shouldUpdate={(prev, next) => prev.condition !== next.condition}>
                            {({ getFieldValue, setFieldsValue }) => (
                                <DiseaseGrid
                                    value={getFieldValue("condition")}
                                    conditions={conditions}
                                    onChange={(value) => {
                                        filterUpdateEvent("condition", value, "icons");
                                        setFieldsValue({ condition: value });
                                        scrollToStep(stepRefs.stepTwo);
                                    }}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            )}
            {!hideLocation && (
                <Row gutter={[16, 16]} ref={stepRefs.stepTwo}>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="location"
                            valuePropName="value"
                            trigger="onChange"
                            label={locationLabel || "What is your location?"}
                        >
                            <LocationAutocomplete initialDisplayValue={initialState?.locationDisplayName} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item name="distanceInMeters" label={distanceLabel || "How far can you travel? (miles)"}>
                            <Radio.Group options={DistanceOptions} optionType="button" buttonStyle="solid" block />
                        </Form.Item>
                    </Col>
                </Row>
            )}
            <Row gutter={[16, 16]} ref={stepRefs.stepThree}>
                {!hideAge && (
                    <Col xs={24} sm={12}>
                        <Form.Item name="age" label={ageLabel || "What is your age?"}>
                            <Radio.Group options={AgeOptions} optionType="button" buttonStyle="solid" block />
                        </Form.Item>
                    </Col>
                )}
                {
                    // When the user selects a gendered option, Algolia includes studies labeled "MALE" and "ALL" OR "FEMALE and "ALL"
                }
                {!hideSex && (
                    <Col xs={24} sm={12}>
                        <Form.Item name="gender" label={sexLabel || "What is your sex?"}>
                            <Radio.Group
                                options={GenderOptions.map((v) => ({
                                    value: v.value,
                                    label: (
                                        <Space>
                                            {v.icon}
                                            {v.label}
                                        </Space>
                                    ),
                                }))}
                                optionType="button"
                                buttonStyle="solid"
                                block
                            />
                        </Form.Item>
                    </Col>
                )}
            </Row>
            {!hideStudyType && (
                <Row ref={stepRefs.stepFour}>
                    <Col>
                        <div className="select-parameter-label main-header main-header-init">
                            {studyTypeLabel || "Are you looking for a Medication, Device, or Survey study?"}
                        </div>
                        <div className="sub-header-light text-center">Select all that apply</div>
                        <Form.Item name="studyTypes">
                            <StudyTypes />
                        </Form.Item>
                    </Col>
                </Row>
            )}
            <Row className={`sticky-buttons ${showSticky ? "active" : ""}`} ref={stepRefs.stepFive} justify="center">
                <Col>
                    <Space size="large" className="search-form-actions">
                        <Button onClick={scrollToTop} type="default">
                            Back to Top
                        </Button>
                        <Button type="primary" htmlType="submit">
                            See My Results
                        </Button>
                    </Space>
                </Col>
            </Row>
        </>
    );
}

export default SearchFormSteps;
